import React from "react";
import { GenericSignal, SignalType } from "@types";
import { getClassName } from "@utils";
import { Avatar, AvatarSize } from "@taskpane/components";
import { getTitle } from "./utils";
import css from "./UsersChangedSignalContent.module.scss";

export interface UsersChangedSignalContentProps {
  signal: GenericSignal<
    | SignalType.ToAdded
    | SignalType.ToRemoved
    | SignalType.CcAdded
    | SignalType.CcRemoved
    | SignalType.ToThirdPartyAdded
    | SignalType.ToThirdPartyRemoved
    | SignalType.CcThirdPartyAdded
    | SignalType.CcThirdPartyRemoved
  >;
}

const UsersChangedSignalContent: React.FC<UsersChangedSignalContentProps> = ({ signal }) => {
  const participants =
    "removed" in signal.eventMetadata
      ? signal.eventMetadata?.removed || []
      : signal.eventMetadata?.added || [];

  const title = getTitle(signal);

  const attrs = {
    container: {
      className: css.container,
    },
    textWrapper: {
      className: css.textWrapper,
    },
    title: {
      className: css.title,
    },
    description: {
      className: css.description,
    },
    content: {
      className: getClassName(
        css.content,
        participants.length > 3 && participants.length < 7 && css.contentMedium,
        participants.length >= 7 && css.contentLarge
      ),
    },
  };

  const renderParticipants = () =>
    participants.map(({ name, email, avatar }, index) => {
      const isParticipantRemoved = "removed" in signal.eventMetadata;
      const [participantFirstName, participantLastName] = name?.split(" ") || [];
      const participantName = participantLastName
        ? `${participantFirstName} ${participantLastName[0]}.`
        : email || "User";
      const attrs = {
        avatarWrapper: {
          className: css.avatarWrapper,
        },
        avatar: {
          size: AvatarSize.XL,
          name: participantName,
          src: avatar,
          className: getClassName(css.avatar, isParticipantRemoved && css.avatarRemoved),
        },
        label: {
          className: getClassName(css.label, isParticipantRemoved && css.labelRemoved),
        },
      };

      return (
        <div {...attrs.avatarWrapper} key={index}>
          <Avatar {...attrs.avatar} />
          <span {...attrs.label}>
            <span>
              {isParticipantRemoved ? "- " : "+ "}
              {participantName}
            </span>
          </span>
        </div>
      );
    });

  return (
    <div {...attrs.container}>
      <div {...attrs.textWrapper}>
        <span {...attrs.title}>{title}</span>
        {"added" in signal.eventMetadata ? (
          <span {...attrs.description}>
            Including additional team members can influence the discussion’s social dynamics.
          </span>
        ) : (
          <span {...attrs.description}>
            Reducing the number of participants can influence the conversation’s social dynamics.
          </span>
        )}
      </div>
      <div {...attrs.content}>{renderParticipants()}</div>
    </div>
  );
};

export default UsersChangedSignalContent;
